export interface CalendarEventData {
  title: string;
  description: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  location?: string;
}

/**
 * Build a Google Calendar "quick add" URL that opens the event pre-filled.
 * The user picks the Gmail account in their browser.
 */
export function buildGoogleCalendarUrl(event: CalendarEventData): string {
  const datePart = event.date.replace(/-/g, '');
  const start = `${datePart}T${event.startTime.replace(':', '')}00`;
  const end = `${datePart}T${event.endTime.replace(':', '')}00`;

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${start}/${end}`,
    details: event.description,
    location: event.location || '',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate a standard iCalendar (.ics) string for one or more events.
 * Compatible with Google Calendar, Outlook, Apple Calendar, etc.
 */
export function generateICSFile(events: CalendarEventData[]): string {
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//LiderIA//Calendario//ES',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];

  for (const event of events) {
    const datePart = event.date.replace(/-/g, '');
    const dtStart = `${datePart}T${event.startTime.replace(':', '')}00`;
    const dtEnd = `${datePart}T${event.endTime.replace(':', '')}00`;
    const uid = `${datePart}-${event.startTime.replace(':', '')}-${Math.random().toString(36).slice(2, 8)}@lideria`;
    const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');

    lines.push(
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      `SUMMARY:${escapeICS(event.title)}`,
      `DESCRIPTION:${escapeICS(event.description)}`,
      event.location ? `LOCATION:${escapeICS(event.location)}` : '',
      'END:VEVENT',
    );
  }

  lines.push('END:VCALENDAR');
  return lines.filter(Boolean).join('\r\n');
}

/** Download an ICS string as a file */
export function downloadICSFile(icsContent: string, filename = 'sesiones-en-vivo.ics') {
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function escapeICS(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}
